import { Reservation } from "@/lib/type";

import React from 'react';

interface BookingConfirmationProps {
  reservation: Reservation;
}

interface FormattedExtra {
  name: string;
  unitPrice: number;
  quantity: number;
  days: number;
  totalPrice: number;
  currency: string;
  isCapped: boolean;
}

// Add this helper function to parse the extra service string
const parseExtraService = (extraString: string): FormattedExtra | null => {
  // New simplified format regex
  const regex = /^(.+) x (\d+) units = ([A-Z]{3}) ([\d,.]+)$/;
  
  const match = extraString.match(regex);
  
  if (match) {
    return {
      name: match[1],
      quantity: parseInt(match[2]),
      currency: match[3],
      totalPrice: parseFloat(match[4].replace(/,/g, '')),
      unitPrice: 0, // Not needed in new format
      days: 1, // Not needed in new format
      isCapped: false // Not needed in new format
    };
  }
  
  return null;
};

const BookingConfirmation: React.FC<BookingConfirmationProps> = ({ reservation }) => {
  const {
    reservation_id = '',
    pickup_location = '',
    pickup_date = '',
    pickup_instructions = '',
    return_location = '',
    return_date = '',
    vehicle_full_name = '',
    vehicle_name = '',
    request_data = {
      driver_name: '',
      driver_phone: '',
      driver_address: '',
      driver_license: {
        number: '',
        expiration_date: '',
        place: '',
      },
      pickup_flight: '',
      NrDays: 0,
    },
    extras_total = '0',
    delivery_total = 0,
    delivery_orders = [],
    amount = '0',
    currency = '',
    amount_with_currency = '',
    extra_services = [],
    payment_method = '',
    payment_status = '',
    payment_date = '',
    status = '',
  } = reservation || {};

  const {
    driver_name = '',
    driver_phone = '',
    driver_address = '',
    driver_license = {},
    pickup_flight = '',
    NrDays = 0,
  } = request_data || {};

  // Helper functions to format dates and times
  const formatDate = (datetime: string): string => {
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
    const date = new Date(datetime);
    return date.toLocaleDateString(undefined, options);
  };

  const formatTime = (datetime: string): string => {
    const options: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };
    const date = new Date(datetime);
    return date.toLocaleTimeString(undefined, options);
  };

  return (
    <div className="max-w-3xl  bg-white ">
      {/* Header Section */}

      {/* Booking Details */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-800">Booking Details</h2>
        <div className="mt-2 border rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <tbody className="bg-white divide-y divide-gray-200">
              <tr>
                <td className="px-4 py-2 text-sm font-medium text-gray-500">Booking Status:</td>
                <td className="px-4 py-2 text-sm text-gray-900">
                  <span className={`${status === 'pending' ? 'text-yellow-500' : status === 'confirmed' ? 'text-green-500' : 'text-red-500'}`}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </span>
                </td>
              </tr>
              <tr className="bg-gray-50">
                <td className="px-4 py-2 text-sm font-medium text-gray-500">Booking Reference No:</td>
                <td className="px-4 py-2 text-sm text-gray-900">{reservation_id}</td>
              </tr>
              <tr>
                <td className="px-4 py-2 text-sm font-medium text-gray-500">Pickup Location:</td>
                <td className="px-4 py-2 text-sm text-gray-900">{pickup_location}</td>
              </tr>
              {pickup_flight && (
                <tr className="bg-gray-50">
                  <td className="px-4 py-2 text-sm font-medium text-gray-500">Flight:</td>
                  <td className="px-4 py-2 text-sm text-gray-900">{pickup_flight}</td>
                </tr>
              )}
              <tr className={pickup_flight ? '' : 'bg-gray-50'}>
                <td className="px-4 py-2 text-sm font-medium text-gray-500">Pickup Date:</td>
                <td className="px-4 py-2 text-sm text-gray-900">{formatDate(pickup_date)}</td>
              </tr>
              <tr className={pickup_flight ? 'bg-gray-50' : ''}>
                <td className="px-4 py-2 text-sm font-medium text-gray-500">Pickup Time:</td>
                <td className="px-4 py-2 text-sm text-gray-900">{formatTime(pickup_date)}</td>
              </tr>
              <tr className={pickup_flight ? '' : 'bg-gray-50'}>
                <td className="px-4 py-2 text-sm font-medium text-gray-500">Pickup Instructions:</td>
                <td className="px-4 py-2 text-sm text-gray-900">{pickup_instructions}</td>
              </tr>
              <tr className={pickup_flight ? 'bg-gray-50' : ''}>
                <td className="px-4 py-2 text-sm font-medium text-gray-500">Return Location:</td>
                <td className="px-4 py-2 text-sm text-gray-900">{return_location}</td>
              </tr>
              <tr className={pickup_flight ? '' : 'bg-gray-50'}>
                <td className="px-4 py-2 text-sm font-medium text-gray-500">Return Date:</td>
                <td className="px-4 py-2 text-sm text-gray-900">{formatDate(return_date)}</td>
              </tr>
              <tr className={pickup_flight ? 'bg-gray-50' : ''}>
                <td className="px-4 py-2 text-sm font-medium text-gray-500">Return Time:</td>
                <td className="px-4 py-2 text-sm text-gray-900">{formatTime(return_date)}</td>
              </tr>
              <tr className={pickup_flight ? '' : 'bg-gray-50'}>
                <td className="px-4 py-2 text-sm font-medium text-gray-500">Number of Rental Day(s):</td>
                <td className="px-4 py-2 text-sm text-gray-900">{reservation.request_data.NrDays.toFixed(0)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Vehicle Details */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-800">Vehicle &amp; Extra Details</h2>
        <ul className="mt-2 space-y-1">
          <li>
            <div className="mt-2 border rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
            <tbody className="bg-white divide-y divide-gray-200">
              <tr>
                <td className="px-4 py-2 text-sm font-medium text-gray-500">Vehicle:</td>
                <td className="px-4 py-2 text-sm text-gray-900">
                {vehicle_full_name || 'N/A'}
                </td>
              </tr>
              </tbody>
            </table>
            </div>
          </li>
          {extra_services && extra_services.length > 0 && (
          <li className="mt-4">
            <strong>Extras:</strong>
            <div className="mt-2 border rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {extra_services.map((extraString, index) => {
                    const extra = parseExtraService(extraString);
                    if (!extra) return null;
                    
                    return (
                      <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-4 py-2 text-sm text-gray-900">{extra.name}</td>
                        <td className="px-4 py-2 text-sm text-gray-900 text-right">{extra.quantity}</td>
                        <td className="px-4 py-2 text-sm text-gray-900 text-right">
                          {extra.currency} {extra.totalPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </li>
        )}
        </ul>
      </div>

      {/* Delivery & Collection */}
      {delivery_orders && delivery_orders.length > 0 && (
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-800">Delivery &amp; Collection</h2>
          <div className="mt-2 border rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Address</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {delivery_orders.map((order, index) => (
                  <tr key={`${order.type}-${index}`}>
                    <td className="px-4 py-2 text-sm text-gray-900 align-top">
                      {order.type === 'pickup' ? 'Delivery to your address' : 'Collection from your address'}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-900 align-top break-words">
                      {order.address}
                      {order.remark && <p className="text-gray-500">Remark: {order.remark}</p>}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-900 text-right align-top whitespace-nowrap">
                      {currency} {Number(order.fee).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Driver's Details */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-800">Driver's Details</h2>
        <div className="mt-2 border rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <tbody className="bg-white divide-y divide-gray-200">
              <tr>
                <td className="px-4 py-2 text-sm font-medium text-gray-500">Name:</td>
                <td className="px-4 py-2 text-sm text-gray-900">{driver_name}</td>
              </tr>
              <tr className="bg-gray-50">
                <td className="px-4 py-2 text-sm font-medium text-gray-500">Phone:</td>
                <td className="px-4 py-2 text-sm text-gray-900">{driver_phone}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Payment Details */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-800">Payment Details</h2>
        <ul className="mt-2 space-y-2">
          {/* Calculate totals */}
          {(() => {
            const extrasTotal = parseFloat(extras_total || '0');
            const deliveryTotal = Number(delivery_total || 0);

            const totalAmount = parseFloat(amount || '0');
            const rentalAmount = totalAmount - extrasTotal - deliveryTotal;

            return (
              <>
                <li className="flex justify-between items-center py-1">
                  <span className="text-gray-600">Rental Amount:</span>
                  <span className="font-medium">{currency} {rentalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </li>
                <li className="flex justify-between items-center py-1">
                  <span className="text-gray-600">Extras Total:</span>
                  <span className="font-medium">{currency} {extrasTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </li>
                {deliveryTotal > 0 && (
                  <li className="flex justify-between items-center py-1">
                    <span className="text-gray-600">Delivery &amp; Collection:</span>
                    <span className="font-medium">{currency} {deliveryTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </li>
                )}
                <li className="flex justify-between items-center py-1 border-t border-gray-200 mt-2 pt-2">
                  <span className="font-semibold">Total Amount:</span>
                  <span className="font-bold">{currency} {totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </li>
                <li className="flex justify-between items-center py-1">
                  <span className="text-gray-600">Payment Method:</span>
                  <span>{payment_method.charAt(0).toUpperCase() + payment_method.slice(1)}</span>
                </li>
                <li className="flex justify-between items-center py-1">
                  <span className="text-gray-600">Payment Status:</span>
                  <span>{payment_status.charAt(0).toUpperCase() + payment_status.slice(1)}</span>
                </li>
                <li className="flex justify-between items-center py-1">
                  <span className="text-gray-600">Payment Date:</span>
                  <span>{formatDate(payment_date)}</span>
                </li>
              </>
            );
          })()}
        </ul>
      </div>



      {/* Contact Information */}
      <div>
        <p className="text-gray-600">
          For any questions or concerns, please contact our customer service team or click the WhatsApp button to chat with us.
        </p>
      </div>
    </div>
  );
};

export default BookingConfirmation;
